import { useState, useRef, useCallback, useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

interface UseVoiceWithVADReturn {
  isListening: boolean;
  isProcessing: boolean;
  isSpeaking: boolean;
  currentTranscript: string;
  lastResponse: string;
  startListening: () => void;
  stopListening: () => void;
  error: string | null;
}

export const useVoiceWithVAD = (positId: string): UseVoiceWithVADReturn => {
  const { user } = useAuth();
  const [isListening, setIsListening] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [currentTranscript, setCurrentTranscript] = useState('');
  const [lastResponse, setLastResponse] = useState('');
  const [error, setError] = useState<string | null>(null);

  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const audioContextRef = useRef<AudioContext | null>(null);
  const analyserRef = useRef<AnalyserNode | null>(null);
  const vadTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  // VAD configuration
  const VAD_THRESHOLD = -45; // dB threshold for speech detection
  const VAD_TIMEOUT = 2000; // ms of silence before stopping

  // Initialize Web Audio API for VAD with mobile optimization
  const initializeAudioContext = useCallback(async () => {
    try {
      // Create audio context with mobile compatibility
      if (!audioContextRef.current) {
        const AudioContext = window.AudioContext || (window as any).webkitAudioContext;
        audioContextRef.current = new AudioContext();
        
        // Mobile browsers require user gesture to start audio context
        if (audioContextRef.current.state === 'suspended') {
          await audioContextRef.current.resume();
        }
      }

      // Request microphone with mobile-optimized constraints
      const constraints = {
        audio: {
          echoCancellation: true,    // Reduce background noise
          noiseSuppression: true,    // Clean audio input
          autoGainControl: true,     // Consistent volume
          sampleRate: 16000,         // Optimal for mobile bandwidth
          channelCount: 1            // Mono for efficiency
        }
      };

      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      
      const source = audioContextRef.current.createMediaStreamSource(stream);
      
      analyserRef.current = audioContextRef.current.createAnalyser();
      analyserRef.current.fftSize = 512;
      analyserRef.current.smoothingTimeConstant = 0.8; // Smooth out mobile noise
      source.connect(analyserRef.current);

      return true;
    } catch (err) {
      
      if (err.name === 'NotAllowedError') {
        setError('Microphone permission denied. Please allow microphone access and try again.');
      } else if (err.name === 'NotFoundError') {
        setError('No microphone found. Please connect a microphone and try again.');
      } else {
        setError('Unable to access microphone. Please check permissions and try again.');
      }
      return false;
    }
  }, []);

  // Voice Activity Detection
  const detectVoiceActivity = useCallback(() => {
    if (!analyserRef.current) return false;

    const bufferLength = analyserRef.current.frequencyBinCount;
    const dataArray = new Uint8Array(bufferLength);
    analyserRef.current.getByteFrequencyData(dataArray);

    // Calculate average volume
    let sum = 0;
    for (let i = 0; i < bufferLength; i++) {
      sum += dataArray[i];
    }
    const average = sum / bufferLength;
    
    // Convert to dB
    const db = 20 * Math.log10(average / 255);
    
    return db > VAD_THRESHOLD;
  }, []);

  // VAD monitoring loop
  const monitorVAD = useCallback(() => {
    if (!isListening) return;

    const hasVoice = detectVoiceActivity();
    
    if (hasVoice) {
      // Clear timeout if voice detected
      if (vadTimeoutRef.current) {
        clearTimeout(vadTimeoutRef.current);
        vadTimeoutRef.current = null;
      }
    } else if (!vadTimeoutRef.current) {
      // Start timeout if no voice
      vadTimeoutRef.current = setTimeout(() => {
        stopListening();
      }, VAD_TIMEOUT);
    }

    // Continue monitoring
    requestAnimationFrame(monitorVAD);
  }, [isListening, detectVoiceActivity]);

  // Process AI response
  const processAIResponse = useCallback(async (transcript: string) => {
    if (!user || !transcript.trim()) return;

    setIsProcessing(true);
    setError(null);

    try {
      // Clean up transcript for common misheard words
      let cleanedTranscript = transcript;
      
      // Fix common misheard variations of "posit" - replace with the correct word
      cleanedTranscript = cleanedTranscript.replace(/\b(closet|deposit|pause it|positive|pos it)\b/gi, 'posit');
      
      // Ensure "posit" is always capitalized when referring to the knowledge base
      cleanedTranscript = cleanedTranscript.replace(/\bposit\b/gi, 'Posit');
      
      if (cleanedTranscript !== transcript) {
      }

      // Call your existing chat API
      const { data, error: chatError } = await supabase.functions.invoke('chat-with-posit-v4-unified', {
        body: {
          message: cleanedTranscript,
          posit_id: positId,
          user_id: user.id,
          conversation_id: null, // Let it create a new one
          voiceMode: true // Add voice mode flag for optimized prompts
        }
      });

      if (chatError) throw chatError;

      const aiResponse = data.content || data.message || "I'm sorry, I couldn't generate a response.";
      setLastResponse(aiResponse);

      // Convert AI response to speech
      await playAIResponse(aiResponse);

    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to process your message');
    } finally {
      setIsProcessing(false);
    }
  }, [user, positId]);

  // Play AI response using OpenAI TTS
  const playAIResponse = useCallback(async (text: string) => {
    try {
      setIsSpeaking(true);

      const response = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/openai-tts`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          text: text,
          voice: 'alloy',
          model: 'tts-1', // Using faster model (vs tts-1-hd)
          response_format: 'mp3',
          speed: 1.1 // 10% faster speech - more natural
        })
      });

      if (!response.ok) {
        throw new Error(`TTS failed: ${response.status}`);
      }

      // Create audio blob and play
      const audioBlob = await response.blob();
      const audioUrl = URL.createObjectURL(audioBlob);
      
      if (audioRef.current) {
        audioRef.current.pause();
      }
      
      audioRef.current = new Audio(audioUrl);
      
      audioRef.current.onloadeddata = () => {
      };
      
      audioRef.current.onended = () => {
        setIsSpeaking(false);
        URL.revokeObjectURL(audioUrl);
      };

      audioRef.current.onerror = (e) => {
        setIsSpeaking(false);
        setError('Audio playback failed');
        URL.revokeObjectURL(audioUrl);
      };

      await audioRef.current.play();

    } catch (err) {
      setIsSpeaking(false);
      setError('Failed to play audio response');
    }
  }, []);

  // Initialize speech recognition with mobile compatibility
  const initializeSpeechRecognition = useCallback(() => {
    // Check for mobile browsers and speech recognition support
    const isMobile = /Android|webOS|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
    const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent);
    const isSafari = /Safari/.test(navigator.userAgent) && !/Chrome/.test(navigator.userAgent);


    // Check speech recognition support
    const SpeechRecognition = window.SpeechRecognition || (window as any).webkitSpeechRecognition;
    
    if (!SpeechRecognition) {
      if (isIOS && isSafari) {
        setError('Voice chat requires Chrome or Firefox on iOS. Safari has limited speech support.');
      } else if (isMobile) {
        setError('Please use Chrome browser for voice chat on mobile');
      } else {
        setError('Speech recognition not supported in this browser');
      }
      return false;
    }

    recognitionRef.current = new SpeechRecognition();
    
    recognitionRef.current.continuous = true;
    recognitionRef.current.interimResults = true;
    recognitionRef.current.lang = 'en-US';

    recognitionRef.current.onstart = () => {
      setCurrentTranscript('');
      setError(null);
    };

    recognitionRef.current.onresult = (event) => {
      let finalTranscript = '';
      let interimTranscript = '';

      for (let i = event.resultIndex; i < event.results.length; i++) {
        const transcript = event.results[i][0].transcript;
        if (event.results[i].isFinal) {
          finalTranscript += transcript;
        } else {
          interimTranscript += transcript;
        }
      }

      setCurrentTranscript(finalTranscript + interimTranscript);

      if (finalTranscript.trim()) {
        processAIResponse(finalTranscript);
      }
    };

    recognitionRef.current.onerror = (event) => {
      setError(`Speech recognition error: ${event.error}`);
      setIsListening(false);
    };

    recognitionRef.current.onend = () => {
      setIsListening(false);
    };

    return true;
  }, [processAIResponse]);

  const startListening = useCallback(async () => {
    if (isListening || isProcessing) return;

    setError(null);
    
    // Initialize audio context for VAD
    const audioInitialized = await initializeAudioContext();
    if (!audioInitialized) return;

    // Initialize speech recognition
    const speechInitialized = initializeSpeechRecognition();
    if (!speechInitialized) return;

    try {
      recognitionRef.current?.start();
      setIsListening(true);
      
      // Start VAD monitoring
      setTimeout(monitorVAD, 100);
    } catch (err) {
      setError('Failed to start voice recognition');
    }
  }, [isListening, isProcessing, initializeAudioContext, initializeSpeechRecognition, monitorVAD]);

  const stopListening = useCallback(() => {
    if (!isListening) return;

    recognitionRef.current?.stop();
    setIsListening(false);

    // Clear VAD timeout
    if (vadTimeoutRef.current) {
      clearTimeout(vadTimeoutRef.current);
      vadTimeoutRef.current = null;
    }
  }, [isListening]);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      recognitionRef.current?.stop();
      audioRef.current?.pause();
      if (vadTimeoutRef.current) {
        clearTimeout(vadTimeoutRef.current);
      }
      audioContextRef.current?.close();
    };
  }, []);

  return {
    isListening,
    isProcessing,
    isSpeaking,
    currentTranscript,
    lastResponse,
    startListening,
    stopListening,
    error
  };
};